import React, { useRef, useEffect } from "react"
import { useAccessContext } from "@/contexts/AccessContext"
import { useMap } from "react-leaflet"
import GeoJsonLayer from "@/components/map/GeoJsonLayer"
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx"
import L from "leaflet"
import "leaflet.markercluster"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { coreGet } from "@/services/coreApi"
import { bindMapCard, useCloseMapCardWhenHidden } from "@/features/map/mapCardStore"
import { createGisCluster, gisMarkerIcon } from "@/features/gis/gisCluster"
import { GIS_MARKERS } from "@/features/gis/gisMarkers"

const MARKER_ICON = gisMarkerIcon(GIS_MARKERS.pilot.Icon, GIS_MARKERS.pilot.color, "pilot-icon")

// Function to filter features based on their visibility within the current map viewport.
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds()
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry
    // Determine visibility based on geometry type:
    // LineString: check if any segment point is within bounds
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) => bounds.contains([coord[1], coord[0]]))
      // MultiLineString: check each line segment
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) => line.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Point: check if the point is within bounds
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      // Polygon: check each ring coordinate
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) => ring.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Fallback: handle other geometry types generically
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      }
      return false
    }
  })
  return filteredFeatures
}

// The GeoDB fills unnamed stations with the placeholder "[Pilot Station]".
const stationName = (name) =>
  name && !/^\[?pilot station\]?$/i.test(name.trim()) ? name : null

export default function PilotStations({
  cwPilotStationGeojson,
  setCwPilotStationGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const pilotStationsEnabled =
    switchGisLayersActive && toggleFlags.includes("PilotStations")
  useCloseMapCardWhenHidden("pilot-", pilotStationsEnabled)

  const map = useMap()
  const suppressed = useGisSuppressed()
  const clusterRef = useRef(null)
  const [clusterVersion, setClusterVersion] = React.useState(0)
  const tooltipCountRef = useRef(0)

  const clearedThisCycleRef = useRef(false)
  const addedKeysRef = useRef(new Set())

  // Reset per-cycle flags when inputs change (prevents duplicate markers in cluster)
  useEffect(() => {
    clearedThisCycleRef.current = false
    addedKeysRef.current = new Set()
  }, [zoomLevel, dragged, resized, cwPilotStationGeojson, toggleFlags, switchGisLayersActive])

  useEffect(() => {
    if (!pilotStationsEnabled) return
    if (cwPilotStationGeojson !== null) return

    let cancelled = false
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/pilot-stations",
          accessContext,
          setAccessContext,
          ".core /geo/pilot-stations error="
        )
        if (!cancelled) {
          setCwPilotStationGeojson(data)
        }
      } catch (error) {
        console.error(error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [
    accessContext,
    cwPilotStationGeojson,
    pilotStationsEnabled,
    setAccessContext,
    setCwPilotStationGeojson,
  ])

  // Create/remove pilot station marker cluster when toggles change; clean up previous cluster
  useEffect(() => {
    if (clusterRef.current) {
      clusterRef.current.clearLayers()
      map.removeLayer(clusterRef.current)
      clusterRef.current = null
    }
    if (pilotStationsEnabled) {
      clusterRef.current = createGisCluster(map, { rgb: GIS_MARKERS.pilot.rgb, icon: GIS_MARKERS.pilot.Icon })
      clusterRef.current.addTo(map)
      setClusterVersion((v) => v + 1)
    }
    return () => {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, pilotStationsEnabled, toggleFlags, switchGisLayersActive])

  useEffect(() => {
    if (!pilotStationsEnabled && cwPilotStationGeojson !== null) {
      setCwPilotStationGeojson(null)
    }
  }, [
    cwPilotStationGeojson,
    pilotStationsEnabled,
    setCwPilotStationGeojson,
  ])

  return !pilotStationsEnabled || cwPilotStationGeojson === null ? null : (
    <GeoJsonLayer
      key={[zoomLevel, dragged, resized, clusterVersion, suppressed]}
      data={filterViewportBoundingbox(map, cwPilotStationGeojson.features)}
      style={{ fillColor: "purple", color: "purple", weight: 2 }}
      onEachFeature={(feature, layer) => {
        if (clusterRef.current && !clearedThisCycleRef.current) {
          clusterRef.current.clearLayers()
          clearedThisCycleRef.current = true
        }
        if (feature.geometry?.coordinates) {
          const [lng, lat] = feature.geometry.coordinates
          const fid = feature?.id || feature?.properties?.id || feature?.properties?.uuid || feature?.properties?.gid
          const fKey = fid != null ? String(fid) : `${lat.toFixed(6)},${lng.toFixed(6)}`
          if (addedKeysRef.current.has(fKey)) return
          addedKeysRef.current.add(fKey)
          const marker = L.marker([lat, lng], { icon: MARKER_ICON, interactive: !suppressed })
          if (!suppressed)
            bindMapCard(
              marker,
              () => ({
                key: `pilot-${fKey}`,
                title: stationName(feature.properties.name) ?? "Pilot station",
                rows: [
                  ["Category", feature.properties.category],
                  ["Channel", feature.properties.comm_channel],
                ],
                text: feature.properties.information,
              }),
              () => L.latLng(lat, lng),
            )
          clusterRef.current?.addLayer(marker)
        }
      }}
      pointToLayer={(feature, latlng) =>
        L.circleMarker(latlng, { radius: 0, opacity: 0, fillOpacity: 0, stroke: false })
      }
      interactive
      transparent
    />
  )
}
